import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react'
import { loginSchema } from '../../schemas/authSchema'
import useAuthStore from '../../stores/useAuthStore'

/**
 * LoginForm Component
 * Handles user login with form validation
 */
const LoginForm = () => {
  const navigate = useNavigate()
  const { login, isLoading, error: authError } = useAuthStore()

  const [showPassword, setShowPassword] = useState(false)
  const [serverError, setServerError] = useState(null)

  // Initialize react-hook-form with Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      remember: false,
    },
  })

  /**
   * Handle form submission
   */
  const onSubmit = async (data) => {
    setServerError(null)

    try {
      console.log('🔵 [LoginForm] Attempting login with:', { username: data.username })
      const result = await login(data)
      console.log('🔵 [LoginForm] Login result:', result)

      if (result.success) {
        console.log('✅ [LoginForm] Login successful')
        console.log('✅ [LoginForm] Checking localStorage...')

        // Wait for Zustand persist to save, then navigate
        // Use requestAnimationFrame to ensure DOM update cycle completes
        requestAnimationFrame(() => {
          setTimeout(() => {
            // Verify state is persisted before navigating
            const persistedState = localStorage.getItem('delivery-auth-storage')
            console.log('✅ [LoginForm] Persisted state exists:', !!persistedState)
            console.log('✅ [LoginForm] Navigating to home...')
            navigate('/', { replace: true })
          }, 200)
        })
      } else {
        console.error('❌ [LoginForm] Login failed:', result.error)
        // Show server error
        setServerError(result.error || 'فشل تسجيل الدخول')
      }
    } catch (error) {
      console.error('❌ [LoginForm] Login exception:', error)
      setServerError('حدث خطأ غير متوقع')
    }
  }

  /**
   * Toggle password visibility
   */
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Server Error Message */}
      {(serverError || authError) && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-800 dark:text-red-200">
              {serverError || authError}
            </p>
          </div>
        </div>
      )}

      {/* Username Field */}
      <div>
        <label
          htmlFor="username"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          اسم المستخدم
        </label>
        <input
          id="username"
          type="text"
          {...register('username')}
          className={`input ${errors.username ? 'input-error' : ''}`}
          placeholder="أدخل اسم المستخدم"
          autoComplete="username"
          disabled={isLoading}
        />
        {errors.username && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {errors.username.message}
          </p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          كلمة المرور
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            {...register('password')}
            className={`input pr-12 ${errors.password ? 'input-error' : ''}`}
            placeholder="أدخل كلمة المرور"
            autoComplete="current-password"
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Remember Me Checkbox */}
      <div className="flex items-center">
        <input
          id="remember"
          type="checkbox"
          {...register('remember')}
          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
          disabled={isLoading}
        />
        <label
          htmlFor="remember"
          className="mr-2 text-sm text-gray-700 dark:text-gray-300"
        >
          تذكرني
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full btn btn-primary touch-target flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>جاري تسجيل الدخول...</span>
          </>
        ) : (
          <>
            <LogIn className="w-5 h-5" />
            <span>تسجيل الدخول</span>
          </>
        )}
      </button>

      {/* Forgot Password Link (Future Feature) */}
      {/* <div className="text-center mt-4">
        <a
          href="/forgot-password"
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          نسيت كلمة المرور؟
        </a>
      </div> */}
    </form>
  )
}

export default LoginForm
